/**
 * Dev-only mock payment rail.
 *
 * On the dev environment there is no brand inventory and the gateway is live,
 * so walking the purchase flow either fails or moves real money. With this
 * enabled, "Pay now" opens the real UPI sheet against fabricated payment
 * options, the real polling state machine runs, and the flow lands on the order
 * screen - without a gateway ever being contacted.
 *
 * It is off unless ALL THREE hold, so a production build cannot reach it:
 *   1. REACT_APP_ENV is not "production"   (.env.production sets it)
 *   2. the API host is api-dev.getfleek.app
 *   3. REACT_APP_MOCK_PAYMENTS is exactly "true"  (.env.development only)
 *
 * Every screen it feeds also renders a "DEV MOCK PAYMENT" badge. A fake
 * success that looks identical to a real one is the whole thing we are trying
 * not to build.
 */

const env = (key) => String(process.env[key] || "");

export const MOCK_PAYMENTS =
  env("REACT_APP_ENV") !== "production" &&
  env("REACT_APP_API_URL").includes("api-dev.") &&
  env("REACT_APP_MOCK_PAYMENTS") === "true";

/* How long the mock stays "pending" before reporting success. Long enough that
   the drawer's polling loop, backoff and pending copy all actually run. */
const SETTLE_AFTER_MS = 4000;

const store = {
  amount: 499,
  orders: new Map(), // orderId -> { placedAt, amount, paymentId }
};

/** Checkout hands us the real payable so the sheet shows a believable figure. */
export const rememberOrderAmount = (amount) => {
  const n = Number(amount);
  if (Number.isFinite(n) && n > 0) store.amount = n;
};

const newId = (prefix) =>
  `${prefix}-${Date.now().toString(36).toUpperCase()}${Math.floor(Math.random() * 900 + 100)}`;

/* A QR-shaped placeholder. Deliberately NOT a scannable code: nothing here
   should be payable, and a mock that scans is a mock that can take money. */
const mockQr = (seed) => {
  const grid = 25;
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const cells = [];
  for (let y = 0; y < grid; y += 1) {
    for (let x = 0; x < grid; x += 1) {
      // Three finder-pattern corners, so it reads as a QR at a glance.
      const finder =
        (x < 7 && y < 7) || (x >= grid - 7 && y < 7) || (x < 7 && y >= grid - 7);
      const onEdge =
        finder &&
        (x % 6 === 0 || y % 6 === 0 || (x > 1 && x < 5 && y > 1 && y < 5) ||
          (x > grid - 6 && x < grid - 2 && y > 1 && y < 5) ||
          (x > 1 && x < 5 && y > grid - 6 && y < grid - 2));
      h = (h * 1103515245 + 12345) >>> 0;
      const on = finder ? onEdge : (h >>> 16) % 100 < 46;
      if (on) cells.push(`<rect x="${x}" y="${y}" width="1" height="1"/>`);
    }
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${grid} ${grid}" shape-rendering="crispEdges">` +
    `<rect width="${grid}" height="${grid}" fill="#fff"/><g fill="#022e40">${cells.join("")}</g></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

/**
 * Reloading the confirmation page must not dead-end. An id this module minted
 * but no longer holds - a refresh, a restored tab - is reconstituted as an
 * order that has already settled.
 */
const recordFor = (orderId) => {
  if (!isMockOrderId(orderId)) return null;
  let record = store.orders.get(orderId);
  if (!record) {
    record = { placedAt: 0, amount: store.amount, paymentId: newId("MOCKPAY") };
    store.orders.set(orderId, record);
  }
  return record;
};

const settled = (orderId) => {
  const record = recordFor(orderId);
  if (!record) return false;
  return Date.now() - record.placedAt >= SETTLE_AFTER_MS;
};

const ok = (data) => Promise.resolve({ data });

/** Sentinel: this call is not ours, let the real API answer it. */
export const PASS_THROUGH = Symbol("pass-through");

const isMockOrderId = (id) => typeof id === "string" && id.startsWith("MOCKORD-");
const isMockPaymentId = (id) => typeof id === "string" && id.startsWith("MOCKPAY-");

const mockOrder = (orderId) => {
  const record = recordFor(orderId) || { amount: store.amount, paymentId: newId("MOCKPAY") };
  const line = {
    id: orderId,
    representation_id: orderId,
    status: "Active",
    updated_at: new Date().toISOString(),
    mrp_price: Math.round(record.amount * 1.4),
    cost: record.amount,
    product_digital: true,
    is_deep_tech: false,
    is_activation_pending: false,
    details: { currency_type: "INR", coin_value_used: 0 },
    user: { phone_number: "", email: "" },
    payment: {
      payment_id: record.paymentId,
      amount: record.amount,
      payment_method: "UPI",
      status: "paid",
    },
    subscription_plan: {
      service_name: "Mock brand",
      plan_name: "Dev mock plan — not a real order",
      subscription_plan_id: "mock-plan",
      mrp_price: Math.round(record.amount * 1.4),
      currency_type: "inr",
      code_redemption_url: "",
    },
  };

  // The design's sample key, so the licence-key block can be reviewed on every
  // screen that shows one. `is_mock` lets those screens label it as a sample.
  const sample = {
    is_mock: true,
    coupon_code: {
      coupon_code: "KL4H-9XQ2-7BTM-VD53",
      coupon_link: "https://kaspersky.co.in",
      time_of_expiry: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString(),
    },
  };
  const item = { ...line, ...sample };
  return {
    ...item,
    new_ordered_items: [item],
    total_amount: record.amount,
    amount_saved_dict: { amount_saved: Math.round(record.amount * 0.4) },
    bundle: null,
  };
};

/**
 * The subset of Api methods the purchase flow touches. Anything not listed
 * here still goes to the real dev API.
 */
export const mockPaymentApi = {
  async PlaceOrder() {
    const orderId = newId("MOCKORD");
    store.orders.set(orderId, {
      placedAt: Date.now(),
      amount: store.amount,
      paymentId: newId("MOCKPAY"),
    });
    return ok({ id: orderId });
  },

  async GetPaymentLinkV4(payload = {}) {
    const orderId = payload.new_placed_order_id;
    const record = store.orders.get(orderId);
    // Re-arm the clock so the sheet always opens in a pending state.
    if (record) record.placedAt = Date.now();
    return ok({
      order_id: orderId,
      payment_id: record?.paymentId || newId("MOCKPAY"),
      gateway: "phonepe_custom",
      amount: record?.amount ?? store.amount,
      is_purchase_done: false,
      // Mirror the gateway: UPI_INTENT on phones (intent URL only, no QR image),
      // UPI_QR on desktop (both). The sheet draws its own QR when none is sent.
      payment_options: {
        ...(payload?.notes?.device_type === "mobile"
          ? { upi_intent: true }
          : { qr_data: mockQr(String(orderId)) }),
        intent_url: `upi://pay?pa=mock@fleek&pn=Fleek%20Dev&am=${record?.amount ?? store.amount}&cu=INR&tn=DEV%20MOCK`,
      },
    });
  },

  async ProcessPaymentV4(payload = {}) {
    const orderId = payload.order_id;
    if (!isMockOrderId(orderId)) return PASS_THROUGH;
    const done = settled(orderId);
    const record = recordFor(orderId);
    return ok({
      order_id: orderId,
      payment_id: record?.paymentId || newId("MOCKPAY"),
      status: done ? "paid" : "pending",
      is_purchase_done: done,
      amount: record?.amount ?? store.amount,
      final_amount_in_inr: record?.amount ?? store.amount,
      currency: "INR",
      gateway: "phonepe_custom",
      payment_method: "UPI",
    });
  },

  async CancelPayment(payload = {}) {
    store.orders.delete(payload.order_id);
    return ok({ status: "cancelled" });
  },

  /* These two are shared with the real order history, so they only answer for
     ids this module minted. Anything else returns PASS_THROUGH and the caller
     hits the real dev API - a mock payment rail must not rewrite the user's
     actual orders. */

  async GetOrderDetailsFromPaymentId(params = {}) {
    const id = params.payment_order_id;
    if (!isMockPaymentId(id)) return PASS_THROUGH;
    const orderId = [...store.orders.keys()].find(
      (key) => store.orders.get(key).paymentId === id
    );
    return ok(orderId ? [mockOrder(orderId)] : []);
  },

  async GetOrderDetails(params) {
    if (!isMockOrderId(params)) return PASS_THROUGH;
    return ok(mockOrder(params));
  },
};
